'use client';

import { useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useMicroSource, PortalLink } from '@akademi/site-builder-runtime';
import type { RendererProps } from '../types';
import { KindBadge } from '../chrome';
import { parseVideoSource } from '../videoSource';
import { useAsyncResource } from '../../../_shared/useAsyncResource';
import { Cinema8Embed, YouTubeEmbed } from './embeds';

/**
 * Popup video renderer — the short-video player. The video sits wide on
 * the left and an info card on the right: kind, title, description,
 * duration, category and topic tags. Category and tags are links into
 * the short-video listing filtered by them (same micro area, popup
 * closes), so a learner reaches "more like this" without leaving the
 * portal. Below ~900px the card wraps under the video.
 *
 * <p>Picks the right element for the URL shape:
 *
 * <ul>
 *   <li>YouTube → iframe at {@code youtube-nocookie.com/embed/<id>}.
 *       YouTube doesn't expose "ended" to the parent frame without
 *       the IFrame Player API, so completion fires immediately on
 *       mount (the user has clearly chosen to watch).</li>
 *   <li>Cinema8 → {@code <cinema8-player>} web component injected via
 *       innerHTML (React can't render the custom element directly
 *       because it owns the shadow-DOM children).</li>
 *   <li>Native file → {@code <video src=...>} with 90%-progress
 *       completion (matches the legacy behaviour).</li>
 *   <li>Unknown URL shape → falls back to {@code <video src>} for
 *       backward compat with hosts that pass a non-extensioned CDN
 *       link.</li>
 * </ul>
 *
 * <p>Reads {@code body.videoUrl} (or {@code body.url}); poster comes
 * from {@code coverImageUrl}; duration from the admin's
 * {@code body.durationLabel}, else the server-extracted card meta.
 */
export function VideoRenderer({ item, onComplete, onClose }: RendererProps) {
  const { t } = useTranslation('site-builder-blocks');
  const micro = useMicroSource();
  const body =
    (item.body as {
      videoUrl?: string;
      url?: string;
      captionsUrl?: string;
      durationLabel?: string;
    }) ?? {};
  const url = body.videoUrl ?? body.url;
  const source = parseVideoSource(url);
  const duration = body.durationLabel?.trim() || item.meta?.duration || null;
  const tags = item.topicTags ?? [];

  // Category name for the card — one small read, cached per popup mount.
  const categories = useAsyncResource(
    async (signal) => (micro && item.categoryId != null ? micro.categories.list({ signal }) : []),
    [micro, item.categoryId],
  );
  const categoryName =
    categories.kind === 'loaded' && item.categoryId != null
      ? (categories.data.find((c) => String(c.id) === String(item.categoryId))?.name ?? null)
      : null;

  const listing = `/lxp/micro-learning/${encodeURIComponent(item.kindSlug)}`;

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'stretch' }}>
      <div
        style={{
          flex: '1 1 560px',
          minWidth: 0,
          background: 'var(--color-neutral-950, #0a0a0a)',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <div style={{ width: '100%', aspectRatio: '16 / 9' }}>
          <VideoPlayer
            source={source}
            posterUrl={item.coverImageUrl ?? undefined}
            captionsUrl={body.captionsUrl}
            onComplete={onComplete}
          />
        </div>
      </div>

      <aside
        aria-label={t('microVideo.info.aria')}
        style={{
          flex: '1 1 300px',
          minWidth: 0,
          padding: 'clamp(18px, 2cqw, 28px)',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        {/* Right-padded so the popup's close / favourite buttons never sit on the chip. */}
        <div style={{ display: 'flex', alignItems: 'center', minHeight: 40, paddingRight: 96 }}>
          <KindBadge kind={item.kind} />
        </div>
        <h2
          style={{
            margin: 0,
            fontSize: 20,
            fontWeight: 800,
            lineHeight: 1.3,
            letterSpacing: '-0.01em',
            color: 'var(--color-text-primary, #18181b)',
          }}
        >
          {item.title}
        </h2>
        {item.description && (
          <p
            style={{
              margin: 0,
              fontSize: 'var(--font-size-sm, 14px)',
              lineHeight: 1.55,
              color: 'var(--color-text-secondary, #52525b)',
            }}
          >
            {item.description}
          </p>
        )}
        {duration && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: '13px',
              fontWeight: 600,
              color: 'var(--color-text-secondary, #52525b)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            <ClockIcon />
            <span>{duration}</span>
          </div>
        )}
        {(categoryName || tags.length > 0) && (
          <div aria-hidden style={{ height: 1, background: 'var(--color-border, #e4e4e7)' }} />
        )}
        {categoryName && item.categoryId != null && (
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}
          >
            <div style={eyebrow}>{t('microVideo.info.category')}</div>
            <PortalLink
              href={`${listing}?category=${encodeURIComponent(String(item.categoryId))}`}
              onClick={onClose}
              title={t('microVideo.info.moreInCategory', { name: categoryName })}
              style={{ ...chipStyle, padding: '6px 10px 6px 8px', gap: 8 }}
            >
              <FolderIcon />
              {categoryName}
            </PortalLink>
          </div>
        )}
        {tags.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={eyebrow}>{t('microVideo.info.tags')}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {tags.map((tag) => (
                <PortalLink
                  key={tag}
                  href={`${listing}?tag=${encodeURIComponent(tag)}`}
                  onClick={onClose}
                  title={t('microVideo.info.moreWithTag', { tag })}
                  style={chipStyle}
                >
                  {tag}
                </PortalLink>
              ))}
            </div>
          </div>
        )}
        {(categoryName || tags.length > 0) && (
          <p
            style={{
              margin: 0,
              fontSize: 'var(--font-size-xs, 12px)',
              color: 'var(--color-text-tertiary, #94867d)',
            }}
          >
            {t('microVideo.info.hint')}
          </p>
        )}
      </aside>
    </div>
  );
}

const eyebrow: CSSProperties = {
  fontSize: '11px',
  fontWeight: 800,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'var(--color-text-tertiary, #71717a)',
};

/** Outlined chip — category and tags share it so the card reads as one
 *  system; the accent is the brand link colour on hover in the design,
 *  which inline styles can't express, so the border carries the affordance. */
const chipStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '6px 11px',
  borderRadius: 8,
  border: '1px solid var(--color-border, #d4d4d8)',
  background: 'var(--color-surface-card, #fff)',
  color: 'var(--color-text-primary, #27272a)',
  fontSize: '13px',
  fontWeight: 600,
  textDecoration: 'none',
  maxWidth: '100%',
};

function ClockIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="15"
      height="15"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function FolderIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="15"
      height="15"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />
    </svg>
  );
}

// -----------------------------------------------------------------------------
// VideoPlayer — picks the element matching the parsed source type.
// -----------------------------------------------------------------------------

function VideoPlayer({
  source,
  posterUrl,
  captionsUrl,
  onComplete,
}: {
  source: ReturnType<typeof parseVideoSource>;
  posterUrl: string | undefined;
  captionsUrl: string | undefined;
  onComplete: () => void;
}) {
  if (source.type === 'youtube') {
    return <YouTubeEmbed embedUrl={source.embedUrl} onComplete={onComplete} />;
  }

  if (source.type === 'cinema8') {
    return (
      <Cinema8Embed
        mediaId={source.mediaId}
        raw={source.raw}
        posterUrl={posterUrl}
        onComplete={onComplete}
      />
    );
  }

  // 'native' (recognised extension) OR 'unknown' (fall back to native
  // player — same behaviour as the pre-port renderer for backward compat).
  const src =
    source.type === 'native' ? source.url : source.type === 'unknown' ? source.url : undefined;
  return (
    <NativeVideo
      src={src}
      posterUrl={posterUrl}
      captionsUrl={captionsUrl}
      onComplete={onComplete}
    />
  );
}

function NativeVideo({
  src,
  posterUrl,
  captionsUrl,
  onComplete,
}: {
  src: string | undefined;
  posterUrl: string | undefined;
  captionsUrl: string | undefined;
  onComplete: () => void;
}) {
  const ref = useRef<HTMLVideoElement | null>(null);
  const [done, setDone] = useState(false);

  const onTime = () => {
    const v = ref.current;
    if (!v || done) return;
    if (v.duration && v.currentTime / v.duration >= 0.9) {
      setDone(true);
      onComplete();
    }
  };

  return (
    <video
      ref={ref}
      src={src}
      poster={posterUrl}
      controls
      onTimeUpdate={onTime}
      style={{ width: '100%', height: '100%' }}
    >
      {captionsUrl && <track kind="captions" src={captionsUrl} default />}
    </video>
  );
}
